/**
 * Print + CSV export helpers for the Business Tracker.
 *
 * Print pattern (matches mockup safePrint):
 *   open a new window, write the report HTML with an "Actions bar" at the
 *   top, the user clicks Save as PDF / Print which fires the browser's
 *   native dialog. Avoids window.print() inside iframes/PWAs which fails
 *   in many embedded contexts.
 *
 * CSV pattern: build the CSV string, drop it into a Blob, trigger an
 * anchor download — no preview step needed.
 */
import { TRACKERS, MONTH_LABELS } from '../config/trackers';
import { BELONGS_META } from '../config/belongs';
import {
    parseCell,
    getCellStatus,
    getCellTotalPaid,
    getCellTotalSales,
    getCellBalance,
    isCellFullyVerified,
    isQuarterlyFilingMonth,
    formatMoney,
} from './cells';
import { totalPaidForAccount } from './derive';

// ---------- generic helpers ----------

function todayStamp() {
    const d = new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

function cellValue(row, fieldName) {
    return (row.fields && row.fields[fieldName]) || '';
}

function csvCell(v) {
    const s = (v == null ? '' : String(v));
    return `"${s.replace(/"/g, '""')}"`;
}

function downloadFile(filename, content, mime) {
    if (typeof window === 'undefined') return;
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// ---------- CSV builders ----------

/**
 * Map a row → ordered cells matching tracker.cols. Mirrors the renderer
 * in TrackerDetail.renderRowCells, just text-only.
 */
function rowAsCells(tracker, row) {
    const f = row.fields || {};
    const status = row.status === 'cancelled' ? 'Cancelled' : 'Active';
    const ytd = totalPaidForAccount(row);

    switch (tracker.key) {
        case 'electricity':
            return [row.label, f.address, f.accountName, f.provider, f.accountNumber, f.ratePerKwh, f.contractEnd, row.paymentMethod, status];
        case 'dumpster':
            return [row.label, f.address, f.accountName, f.vendor, f.accountNumber, f.planSize, f.monthlyCost, f.contractEnd, row.paymentMethod, status];
        case 'insurance':
            return [row.label, f.address, f.insuranceCompany, f.policyNumber, f.policyType, f.termLength, f.totalPremium, f.expirationDate, row.paymentMethod, status];
        case 'alarm':
            return [row.label, f.address, f.accountName, f.alarmCompany, f.accountNumber, f.planService, f.monthlyCost, f.contractEnd, row.paymentMethod, status];
        case 'pest':
            return [row.label, f.address, f.accountName, f.provider, f.accountNumber, f.serviceFrequency, f.costPerService, f.contractEnd, row.paymentMethod, status];
        case 'mortgage':
            return [row.label, f.address, f.mortgageType, f.lender, f.accountNumber, f.monthlyPayment, f.leaseEndDate || f.originalLoanAmount || '', row.paymentMethod, status];
        case 'tenantRent':
            return [
                row.label,
                f.tenantName,
                `${f.leaseStart || ''} → ${f.leaseEnd || ''}`,
                f.monthlyRent,
                f.securityDeposit,
                f.lateFeeAmount && f.lateFeeAfterDay ? `$${f.lateFeeAmount} after day ${f.lateFeeAfterDay}` : '',
                getRowBalance(tracker, row),
                status,
            ];
        case 'salesTax':
            return [row.label, f.address, f.filingFrequency, f.permitNumber, f.filingMethod, f.lastFiledDate, f.nextDueDate, row.paymentMethod, status];
        case 'propertyTax':
            return [row.label, f.address, f.county, f.schoolDistrict, f.appraisedValue, f.homestead, formatMoney(ytd) || '$0', row.paymentMethod, status];
        case 'annualTax':
            return [row.label, f.parentCompany, f.ownership, f.entityType, f.ein, f.annualizedRevenue, f.franchiseFiling, f.federalForm, status];
        default:
            return tracker.cols.map(() => '');
    }
}

/** Approximate balance owed for tenantRent (used in CSV only). */
function getRowBalance(tracker, row) {
    if (tracker.key !== 'tenantRent') return '';
    const cells = row.months || [];
    let balance = 0;
    cells.forEach((raw) => {
        const parsed = parseCell(raw);
        const status = getCellStatus(parsed);
        if (status === 'partial' || status === 'pending' || status === 'overdue') {
            balance += getCellBalance(parsed) || parsed.expected || 0;
        }
    });
    return balance > 0 ? formatMoney(balance) : '$0';
}

export function exportAccountsCSV(tracker, rows) {
    const headers = tracker.cols;
    let csv = `${headers.map(csvCell).join(',')}\n`;
    rows.forEach((row) => {
        csv += `${rowAsCells(tracker, row).map(csvCell).join(',')}\n`;
    });
    const filename = `${tracker.label.replace(/[^A-Za-z0-9]/g, '')}-accounts-${todayStamp()}.csv`;
    downloadFile(filename, csv, 'text/csv;charset=utf-8');
    return filename;
}

export function exportPaymentsCSV(tracker, rows, year) {
    const isAnnual = tracker.frequency === 'annual';
    const slotLabels = isAnnual
        ? (tracker.subTaxLabels || ['Slot 1', 'Slot 2', 'Slot 3', 'Slot 4', 'Slot 5'])
        : MONTH_LABELS;
    const slotCount = isAnnual ? 5 : 12;

    const headers = ['Location', ...slotLabels.flatMap((l) => [`${l} Amount`, `${l} Date`, `${l} Status`]), 'YTD Total'];
    let csv = `${headers.map(csvCell).join(',')}\n`;

    rows.forEach((row) => {
        const cells = isAnnual ? (row.subTaxes || []) : (row.months || []);
        let ytd = 0;
        const out = [csvCell(row.label || '')];
        for (let i = 0; i < slotCount; i += 1) {
            const raw = cells[i] || 'future';
            if (raw === 'future' || raw === 'na') {
                out.push(csvCell(''), csvCell(''), csvCell(raw));
                continue;
            }
            const parsed = parseCell(raw);
            const status = getCellStatus(parsed);
            const tp = getCellTotalPaid(parsed);
            if (status === 'paid' || status === 'partial') ytd += tp;
            const date = parsed.payments && parsed.payments[0] ? parsed.payments[0].date : '';
            out.push(csvCell(tp || parsed.expected), csvCell(date), csvCell(status));
        }
        out.push(csvCell(ytd));
        csv += `${out.join(',')}\n`;
    });

    const filename = `${tracker.label.replace(/[^A-Za-z0-9]/g, '')}-payments-${year}-${todayStamp()}.csv`;
    downloadFile(filename, csv, 'text/csv;charset=utf-8');
    return filename;
}

// ---------- HTML print builders ----------

const PRINT_STYLES = `
* { box-sizing: border-box; margin: 0; padding: 0; }
body {
    font-family: 'Nunito Sans', Arial, sans-serif;
    color: #1C2B24;
    padding: 0;
    margin: 0 auto;
    max-width: 1080px;
}
.actions-bar {
    position: sticky; top: 0; z-index: 99;
    background: #2D5A3D; color: white;
    padding: 12px 20px;
    display: flex; gap: 10px; align-items: center;
    box-shadow: 0 2px 8px rgba(0,0,0,.2);
}
.actions-bar h3 { font-size: 14px; flex: 1; margin: 0; font-weight: 700; }
.actions-bar button {
    background: white; color: #2D5A3D;
    border: none; padding: 8px 16px; border-radius: 6px;
    font-weight: 700; cursor: pointer; font-size: 13px;
}
.doc-header {
    background: #0F1B14; color: white;
    padding: 22px 28px 26px;
}
.doc-eyebrow {
    font-family: 'Nunito', sans-serif;
    font-size: 10px; font-weight: 700; letter-spacing: 2px;
    color: #9BB0A8; margin-bottom: 6px;
}
.doc-title {
    font-family: 'Nunito', sans-serif;
    font-size: 24px; font-weight: 800; color: white;
    letter-spacing: -.3px; margin-bottom: 8px;
}
.doc-meta { font-size: 12px; color: #C7D2C9; }
.doc-body { padding: 22px 28px 32px; }
.stat-cards {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px; margin-bottom: 22px;
}
.stat-card { border-radius: 8px; padding: 14px 16px; }
.stat-card.dark { background: #0F1B14; color: white; }
.stat-card.green { background: #ECFDF5; color: #065F46; }
.stat-card.red { background: #FEE2E2; color: #B91C1C; }
.stat-card.amber { background: #FEF3C7; color: #92400E; }
.stat-label {
    font-size: 9px; font-weight: 800; letter-spacing: 1.5px;
    text-transform: uppercase; margin-bottom: 4px; opacity: .85;
}
.stat-value {
    font-family: 'Nunito', sans-serif;
    font-size: 20px; font-weight: 800; letter-spacing: -.3px;
}
table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 16px; }
th {
    background: #F1F5F9; font-weight: 700; text-align: left;
    padding: 10px 12px; border-bottom: 1px solid #E2E8F0;
    color: #1C2B24;
}
td { padding: 10px 12px; border-bottom: 1px solid #F1F5F9; vertical-align: top; }
td.amt { text-align: right; font-weight: 700; }
td.amt-pos { text-align: right; font-weight: 700; color: #B91C1C; }
td.amt-neg { text-align: right; font-weight: 700; color: #15803D; }
.doc-footer {
    display: flex; justify-content: space-between;
    padding: 14px 28px; background: #FAFBFA;
    border-top: 1px solid #E2E8F0;
    font-size: 10px; color: #6B8278;
}
@media print {
    .actions-bar { display: none !important; }
    @page { margin: 0.4in; }
}
@media print and (orientation: landscape) {
    @page { size: landscape; margin: 0.35in; }
}
`;

function escapeHtml(s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function docHeader(title, subtitle) {
    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    return `
<div class="doc-header">
  <div class="doc-eyebrow">BEENEXA · BUSINESS TRACKER</div>
  <div class="doc-title">${escapeHtml(title)}</div>
  <div class="doc-meta">${subtitle ? `${escapeHtml(subtitle)} · ` : ''}${date}</div>
</div>
<div class="doc-body">`;
}

function docFooter() {
    return `</div>
<div class="doc-footer">
  <span>Auto-generated by BeeNexa Business Tracker</span>
  <span>Amounts in USD</span>
</div>`;
}

function openPrintWindow(html, filename, landscape) {
    if (typeof window === 'undefined') return;
    const sizeRule = landscape ? '@page { size: landscape; margin: 0.35in; }' : '@page { margin: 0.4in; }';
    const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${escapeHtml(filename)}</title>
<style>${PRINT_STYLES}\n${sizeRule}</style>
</head>
<body>
<div class="actions-bar">
  <h3>${escapeHtml(filename)}${landscape ? ' · Landscape' : ''}</h3>
  <button onclick="window.print()">Print / Save as PDF</button>
  <button onclick="window.close()">Close</button>
</div>
${html}
</body></html>`;
    const w = window.open('', '_blank');
    if (w && w.document) {
        w.document.open();
        w.document.write(fullHtml);
        w.document.close();
    }
}

// ---------- Print: Accounts ----------

export function printAccounts(tracker, rows, filterLabel) {
    const total = rows.length;
    const active = rows.filter((r) => r.status !== 'cancelled').length;
    const cancelled = total - active;

    let html = docHeader(`${tracker.label} Accounts Report`, filterLabel ? `Filter: ${filterLabel}` : null);
    html += `
<div class="stat-cards">
  <div class="stat-card dark"><div class="stat-label">Total Accounts</div><div class="stat-value">${total}</div></div>
  <div class="stat-card green"><div class="stat-label">Active</div><div class="stat-value">${active}</div></div>
  <div class="stat-card amber"><div class="stat-label">Cancelled</div><div class="stat-value">${cancelled}</div></div>
</div>`;

    html += '<table><thead><tr>';
    tracker.cols.forEach((c) => { html += `<th>${escapeHtml(c)}</th>`; });
    html += '</tr></thead><tbody>';
    rows.forEach((row) => {
        html += '<tr>';
        rowAsCells(tracker, row).forEach((c) => {
            html += `<td>${escapeHtml(c == null ? '' : c)}</td>`;
        });
        html += '</tr>';
    });
    html += '</tbody></table>';
    html += docFooter();

    const filename = `${tracker.label.replace(/[^A-Za-z0-9]/g, '')}-Accounts-${todayStamp()}`;
    openPrintWindow(html, filename, false);
    return filename;
}

// ---------- Print: Payments ----------

export function printPayments(tracker, rows, year, filterLabel) {
    const isAnnual = tracker.frequency === 'annual';
    const slotLabels = isAnnual
        ? (tracker.subTaxLabels || ['Slot 1', 'Slot 2', 'Slot 3', 'Slot 4', 'Slot 5'])
        : MONTH_LABELS;
    const slotCount = isAnnual ? 5 : 12;

    // Aggregate totals.
    let totalPaid = 0; let totalPending = 0; let totalOverdue = 0;
    rows.forEach((row) => {
        const cells = isAnnual ? (row.subTaxes || []) : (row.months || []);
        for (let i = 0; i < slotCount; i += 1) {
            const raw = cells[i] || 'future';
            if (raw === 'future' || raw === 'na') continue;
            const parsed = parseCell(raw);
            const status = getCellStatus(parsed);
            const tp = getCellTotalPaid(parsed);
            totalPaid += tp;
            if (status === 'pending') totalPending += parsed.expected || 0;
            else if (status === 'overdue') totalOverdue += parsed.expected || 0;
            else if (status === 'partial') totalPending += getCellBalance(parsed);
        }
    });

    let html = docHeader(
        `${tracker.label} ${tracker.incoming ? 'Income' : 'Payment'} Report`,
        `Year ${year}${filterLabel ? ` · ${filterLabel}` : ''}`,
    );
    html += `
<div class="stat-cards">
  <div class="stat-card green"><div class="stat-label">${tracker.incoming ? 'Total Received' : 'Total Paid'}</div><div class="stat-value">${formatMoney(totalPaid)}</div></div>
  <div class="stat-card amber"><div class="stat-label">Pending</div><div class="stat-value">${formatMoney(totalPending)}</div></div>
  <div class="stat-card red"><div class="stat-label">${tracker.incoming ? 'Overdue' : 'Outstanding'}</div><div class="stat-value">${formatMoney(totalOverdue)}</div></div>
</div>`;

    html += '<table><thead><tr><th>Location</th>';
    slotLabels.forEach((l) => { html += `<th style="text-align:center;">${escapeHtml(l)}</th>`; });
    html += '<th class="amt">YTD</th></tr></thead><tbody>';

    rows.forEach((row) => {
        const cells = isAnnual ? (row.subTaxes || []) : (row.months || []);
        let ytd = 0;
        html += `<tr><td><strong>${escapeHtml(row.label || '')}</strong></td>`;
        for (let i = 0; i < slotCount; i += 1) {
            const raw = cells[i] || 'future';
            if (raw === 'future') {
                html += '<td style="text-align:center;color:#9BB0A8;">—</td>';
                continue;
            }
            if (raw === 'na') {
                html += '<td style="text-align:center;color:#9BB0A8;">N/A</td>';
                continue;
            }
            const parsed = parseCell(raw);
            const status = getCellStatus(parsed);
            const tp = getCellTotalPaid(parsed);
            ytd += tp;
            let display = formatMoney(tp || parsed.expected || 0);
            let color = '#15803D'; let sub = '';
            if (status === 'partial') { color = '#92400E'; sub = `${parsed.payments.length} pmts · -${formatMoney(getCellBalance(parsed))}`; }
            else if (status === 'paid') { color = '#15803D'; sub = isCellFullyVerified(parsed) ? '✓ verified' : '✓'; }
            else if (status === 'overdue') { color = '#B91C1C'; sub = 'overdue'; display = formatMoney(parsed.expected || 0); }
            else if (status === 'pending') { color = '#D97706'; sub = 'pending'; display = formatMoney(parsed.expected || 0); }

            const isQuarterly = tracker.key === 'salesTax' && row.fields?.filingFrequency === 'Quarterly';
            if (!isAnnual && isQuarterly && !isQuarterlyFilingMonth(i)) {
                html += '<td style="text-align:center;color:#9BB0A8;">N/A</td>';
                continue;
            }

            const sales = getCellTotalSales(parsed);
            const salesLine = sales > 0 ? `<div style="font-size:9px;color:#15803D;">${formatMoney(sales)} sales</div>` : '';
            html += `<td style="text-align:center;"><div style="font-weight:700;color:${color};">${display}</div><div style="font-size:9px;color:#6B8278;">${escapeHtml(sub)}</div>${salesLine}</td>`;
        }
        html += `<td class="amt-neg">${formatMoney(ytd)}</td></tr>`;
    });

    html += '</tbody></table>';
    html += docFooter();

    const filename = `${tracker.label.replace(/[^A-Za-z0-9]/g, '')}-Payments-${year}-${todayStamp()}`;
    openPrintWindow(html, filename, true);
    return filename;
}

// ---------- Print: Single account history ----------

export function printSingleAccount(tracker, row) {
    const monthlyEst = (() => {
        const f = row.fields || {};
        const candidates = [
            f.monthlyPayment, f.totalPremium, f.monthlyCost, f.monitoringCost,
            f.costPerService, f.monthlyRent, f.ratePerKwh,
        ];
        for (const c of candidates) {
            const n = Number(String(c || '').replace(/[^\d.]/g, ''));
            if (n > 0) return n;
        }
        return 0;
    })();
    const annualObligation = monthlyEst * 12;
    const ytdPaid = totalPaidForAccount(row);
    let outstanding = 0;
    let progressPct = 0;
    const cells = tracker.frequency === 'annual' ? (row.subTaxes || []) : (row.months || []);
    cells.forEach((raw) => {
        if (raw === 'future' || raw === 'na' || !raw) return;
        const parsed = parseCell(raw);
        const status = getCellStatus(parsed);
        if (status === 'partial' || status === 'pending' || status === 'overdue') {
            outstanding += getCellBalance(parsed) || parsed.expected || 0;
        }
    });
    if (annualObligation > 0) {
        progressPct = Math.min(100, Math.round((ytdPaid / annualObligation) * 100));
    }

    let html = docHeader(`${tracker.label} Statement`, row.label);
    html += `
<div class="stat-cards">
  <div class="stat-card dark"><div class="stat-label">Annual Obligation</div><div class="stat-value">${formatMoney(annualObligation)}</div></div>
  <div class="stat-card green"><div class="stat-label">Paid YTD</div><div class="stat-value">${formatMoney(ytdPaid)}</div></div>
  <div class="stat-card red"><div class="stat-label">${tracker.incoming ? 'Balance Owed' : 'Outstanding'}</div><div class="stat-value">${formatMoney(outstanding)}</div></div>
</div>`;

    if (annualObligation > 0) {
        html += `
<div style="margin-bottom:22px;">
  <div style="display:flex;justify-content:space-between;font-size:10px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;color:#6B8278;margin-bottom:6px;">
    <span>Payment Progress</span><span>${progressPct}%</span>
  </div>
  <div style="background:#E2E8F0;height:6px;border-radius:3px;overflow:hidden;">
    <div style="height:100%;background:#2D5A3D;width:${progressPct}%;"></div>
  </div>
</div>`;
    }

    // Account details
    html += '<table><thead><tr><th style="width:35%;">Field</th><th>Value</th></tr></thead><tbody>';
    html += `<tr><td><strong>Belongs To</strong></td><td>${escapeHtml(BELONGS_META[row.belongs]?.label || row.belongs || '')}</td></tr>`;
    html += `<tr><td><strong>Status</strong></td><td>${row.status === 'cancelled' ? 'Cancelled' : 'Active'}</td></tr>`;
    html += `<tr><td><strong>Payment Method</strong></td><td>${escapeHtml(row.paymentMethod || 'Manual')}</td></tr>`;
    (tracker.fields || [])
        .filter((f) => f.name !== 'label' && f.name !== 'paymentMethod' && f.name !== 'autoPayDay')
        .forEach((f) => {
            const v = cellValue(row, f.name);
            if (!v) return;
            html += `<tr><td><strong>${escapeHtml(f.label)}</strong></td><td>${escapeHtml(v)}</td></tr>`;
        });
    html += '</tbody></table>';

    // Payment history
    const slotLabels = tracker.frequency === 'annual'
        ? (tracker.subTaxLabels || ['Slot 1', 'Slot 2', 'Slot 3', 'Slot 4', 'Slot 5'])
        : MONTH_LABELS;
    html += '<table><thead><tr><th>Period</th><th>Date</th><th>Type</th><th class="amt">Amount</th></tr></thead><tbody>';
    cells.forEach((raw, i) => {
        if (raw === 'future' || raw === 'na' || !raw) return;
        const parsed = parseCell(raw);
        const status = getCellStatus(parsed);
        const slot = slotLabels[i] || `Slot ${i + 1}`;
        if (parsed.payments && parsed.payments.length > 0) {
            parsed.payments.forEach((p, pi) => {
                const dateFmt = p.date
                    ? new Date(`${p.date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : slot;
                const note = parsed.payments.length > 1 ? `Payment ${pi + 1} of ${parsed.payments.length}` : 'Payment';
                html += `<tr><td>${escapeHtml(slot)}</td><td>${escapeHtml(dateFmt)}</td><td>${escapeHtml(note)}</td><td class="amt-neg">${formatMoney(p.amt)}</td></tr>`;
            });
            if (status === 'partial') {
                html += `<tr style="background:#FEF3C7;"><td>${escapeHtml(slot)}</td><td>—</td><td>Balance owed</td><td class="amt-pos">${formatMoney(getCellBalance(parsed))}</td></tr>`;
            }
        } else {
            const typeLabel = status === 'overdue' ? 'Overdue' : 'Pending';
            html += `<tr><td>${escapeHtml(slot)}</td><td>—</td><td>${typeLabel}</td><td class="amt-pos">${formatMoney(parsed.expected || 0)}</td></tr>`;
        }
    });
    html += '</tbody></table>';
    html += docFooter();

    const filename = `Statement-${(row.label || 'account').replace(/[^A-Za-z0-9]/g, '_')}-${todayStamp()}`;
    openPrintWindow(html, filename, false);
    return filename;
}

// Re-export TRACKERS for convenience (callers usually have it already, but
// some print flows match by trackerKey only).
export { TRACKERS };
